import type { WalletAddressFormSchema } from 'BreetComponents';
import {
	checkNearestValidAmount,
	type CURRENCYCODETYPE,
	type CURRENCYSYMBOLTYPE,
	formatNumberWithCommas,
	getCleanedValue,
	handleDecimalPoints,
	type IUseFormData,
	type IWalletProps,
} from 'BreetHelpers';
import { type ChangeEvent, useCallback, useState } from 'react';

export const useCryptoFormAmount = ({
	withdrawalMaxAmount,
	withdrawalMinAmount,
	formDataContextProps,
	userWalletProps,
	currencySymbol,
	currencyCode,
}: {
	withdrawalMaxAmount: number;
	withdrawalMinAmount: number;
	formDataContextProps: IUseFormData<typeof WalletAddressFormSchema>;
	userWalletProps: IWalletProps;
	currencySymbol: CURRENCYSYMBOLTYPE;
	currencyCode: CURRENCYCODETYPE;
}) => {
	const [inputMessage, setInputMessage] = useState('');

	const {
		hookFormProps: {
			setError,
			clearErrors,
			formState: { errors },
		},
		setValueAndValidate,
	} = formDataContextProps;

	const validateAmount = useCallback(
		(cleanedValue: string) => {
			const inputAmount = Number(cleanedValue.replace(/,/g, ''));

			if (inputAmount > userWalletProps.walletBalance) {
				setError('amount', {
					message: 'Insufficient balance',
				});
				return;
			}

			if (inputAmount < withdrawalMinAmount) {
				setError('amount', {
					message: `The minimum withdrawal amount is ${currencySymbol} ${formatNumberWithCommas(withdrawalMinAmount)}`,
				});
				return;
			}

			if (!!withdrawalMaxAmount && inputAmount > withdrawalMaxAmount) {
				setError('amount', {
					message: `The maximum withdrawal amount is ${currencySymbol} ${formatNumberWithCommas(withdrawalMaxAmount)}`,
				});
				return;
			}

			if (currencySymbol === '₦') {
				const nearestValidAmountError = checkNearestValidAmount(inputAmount, currencyCode);

				if (nearestValidAmountError) {
					clearErrors('amount');
					setError('amount', { message: nearestValidAmountError });
					return;
				}
			}

			clearErrors('amount');
		},
		[userWalletProps.walletBalance, withdrawalMinAmount, withdrawalMaxAmount, currencyCode, clearErrors, setError, currencySymbol]
	);

	const commitAmount = useCallback(
		(value: string) => {
			let cleanedValue = getCleanedValue(value);

			cleanedValue = handleDecimalPoints(cleanedValue);

			setValueAndValidate('amount', cleanedValue)
				.then(() => {
					validateAmount(cleanedValue);
				})
				.catch(console.error);
		},
		[validateAmount, setValueAndValidate]
	);

	const handleAmountChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const { value } = event.target;

			if (!!errors.amount?.message || !value) clearErrors('amount');

			commitAmount(value);

			if (inputMessage) {
				setInputMessage('');
			}
		},
		[errors.amount?.message, clearErrors, commitAmount, inputMessage]
	);

	return {
		handleAmountChange,
		inputMessage,
		setInputMessage,
		commitAmount,
	};
};
